/**
 * Per-type answer controls (brief §practice): mc radio/checkbox, numeric
 * `NumericInput` per part, open textarea → "Klar" / "Ge upp" → rubric
 * self-grading. Fully controlled — `SessionPage` owns the draft state so the
 * same values back both the UI and the keyboard shortcuts.
 */
import { useId, type Ref } from "react";
import type { ConfidenceLevel } from "@shared/attemptAnswer";
import type { PublicQuestion } from "@shared/types";
import { Figure } from "@client/components/Figure";
import { Localized, useLocalizedText } from "@client/components/Localized";
import { NumericInput } from "@client/components/NumericInput";
import { Button, Checkbox, Input, RadioGroup, Textarea } from "@client/components/ui";
import { useT } from "@client/i18n";
import { mathOnlyReadable } from "@client/lib/readableMath";
import { answerKeyDown } from "./answerKeys";
import { ConfidenceControl, type ConfidenceControlHandle } from "./ConfidenceControl";
import { SegmentedNumeric, type SegmentedNumericProps } from "./SegmentedNumeric";
import {
  MISSED_CHIPS,
  OPEN_TEXT_MAX,
  openBelowFullMarks,
  type MissedChip,
  type QuestionResult,
} from "./session";

export { OPEN_TEXT_MAX };
/** Server-side cap on an MC justification (`practice/routes.ts`). */
const JUSTIFICATION_MAX = 2000;

export interface AnswerAreaProps {
  question: PublicQuestion;
  result: QuestionResult;
  mcSelected: string[];
  onMcChange: (selected: string[]) => void;
  numericValues: Record<string, string>;
  onNumericChange: (partId: string, value: string) => void;
  openText: string;
  onOpenTextChange: (value: string) => void;
  openTicked: string[];
  onOpenTickedChange: (ticked: string[]) => void;
  onSubmit: () => void;
  onOpenKlar: () => void;
  onOpenGiveUp: () => void;
  onOpenSave: () => void;
  /** Open, after "Klar": the correction written after seeing the solution. */
  openCorrection?: string;
  onOpenCorrectionChange?: (value: string) => void;
  /** Open, after "Klar": which principle or condition the student missed. */
  openMissed?: string;
  onOpenMissedChange?: (value: string) => void;
  /** Inline error on the missed field, set by the page when a save was refused. */
  openMissedError?: string;
  /** The quick answer picked for "what did you miss" (P1-9); one satisfies the requirement. */
  openMissedChip?: MissedChip | undefined;
  onOpenMissedChipChange?: (chip: MissedChip) => void;
  /** MC: the justification draft. */
  justification?: string;
  onJustificationChange?: (value: string) => void;
  /** MC: inline error when a required justification was missing at submit. */
  justificationError?: string;
  /** "Hur säker är du?": the page stores the pick in `QuestionResult.confidence`. */
  onConfidenceChange?: (level: ConfidenceLevel) => void;
  /** Inline error when a submit was refused for a missing rating. */
  confidenceError?: string;
  /** Lets the page move focus to the rating when it refuses a submit. */
  confidenceRef?: Ref<ConfidenceControlHandle>;
  /** H from a focused option or rating (the window shortcuts stand down on inputs). */
  onRevealHint?: () => void;
  /** Focused when a submit is refused for a missing justification (I1: WCAG 3.3.1/4.1.3). */
  justificationFieldRef?: Ref<HTMLTextAreaElement>;
  /** Focused when a save is refused for a missing "what did you miss" (I1: WCAG 3.3.1/4.1.3). */
  openMissedFieldRef?: Ref<HTMLInputElement>;
  submitting?: boolean;
  revealing?: boolean;
  /**
   * Retry mode (step 2.4b): the first try was withheld. The inputs of the wrong parts re-open, the
   * right parts stay locked with their first value, a single-select MC shuts its first choice, and
   * there is no rating and no "Svara": the retry panel owns the buttons.
   */
  retry?:
    | {
        keptPartIds: readonly string[];
        /** What was typed for each part the first time. */
        keptValues: Readonly<Record<string, string>>;
        /** MC: the option picked first. */
        firstChoice?: readonly string[];
      }
    | undefined;
  /** Segmented mode (B4): numeric parts checked one at a time. Absent = every part at once. */
  segmented?: Omit<SegmentedNumericProps, "parts"> | undefined;
  /** The per-viewer switch between the two; shown only for a numeric item with several parts. */
  showAll?: { checked: boolean; onChange: (on: boolean) => void; disabled?: boolean } | undefined;
}

export function AnswerArea({
  question,
  result,
  mcSelected,
  onMcChange,
  numericValues,
  onNumericChange,
  openText,
  onOpenTextChange,
  openTicked,
  onOpenTickedChange,
  onSubmit,
  onOpenKlar,
  onOpenGiveUp,
  onOpenSave,
  openCorrection = "",
  onOpenCorrectionChange = () => {},
  openMissed = "",
  onOpenMissedChange = () => {},
  openMissedError,
  openMissedChip,
  onOpenMissedChipChange = () => {},
  justification = "",
  onJustificationChange = () => {},
  justificationError,
  onConfidenceChange = () => {},
  confidenceError,
  confidenceRef,
  onRevealHint,
  justificationFieldRef,
  openMissedFieldRef,
  submitting,
  revealing,
  retry,
  segmented,
  showAll,
}: AnswerAreaProps) {
  const t = useT();
  const localize = useLocalizedText();
  const preRevealHeadingId = useId();
  // P1-8: the answer field names the keyboard path to the rating ("1–4, then Enter").
  const keysHintId = useId();

  /** One rating per answer, just before the step's primary button (pedagogy spec §4.3.2). */
  const rating = (submit: () => void) => (
    <div>
      <ConfidenceControl
        ref={confidenceRef}
        value={result.confidence}
        onChange={onConfidenceChange}
        error={confidenceError}
        onSubmit={submit}
        onRevealHint={onRevealHint}
      />
      <p id={keysHintId} className="mt-1 text-xs text-ink-500 dark:text-ink-400 max-sm:sr-only">
        {t("practice.confidence.keysHint")}
      </p>
    </div>
  );

  if (result.terminal) return null;

  const firstChoice = retry?.firstChoice ?? [];

  if (question.type === "mc") {
    return (
      <div className="mt-4 space-y-3">
        {/* The options are <input>s, where the window shortcuts stand down
            (lib/keyboard.ts): Enter/S submit and H opens a hint from here. */}
        <div onKeyDown={answerKeyDown({ submit: onSubmit, hint: onRevealHint })}>
          {question.multiple ? (
            <div className="flex flex-col gap-2">
              {question.options.map((option) => (
                <Checkbox
                  key={option.id}
                  label={<Localized value={option.text} variant="inline" />}
                  aria-label={mathOnlyReadable(localize(option.text))}
                  checked={mcSelected.includes(option.id)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    onMcChange(
                      checked
                        ? [...mcSelected, option.id]
                        : mcSelected.filter((id) => id !== option.id),
                    );
                  }}
                />
              ))}
            </div>
          ) : (
            <RadioGroup
              value={mcSelected[0] ?? null}
              onChange={(value) => onMcChange([value])}
              options={question.options.map((option) => {
                // The first choice is shut on the retry and says so, in its name too.
                const first = firstChoice.includes(option.id);
                const firstNote = t("practice.retry.firstChoice");
                const readable = mathOnlyReadable(localize(option.text));
                return {
                  value: option.id,
                  label: (
                    <>
                      <Localized value={option.text} variant="inline" />
                      {first && (
                        <span className="text-xs text-ink-600 dark:text-ink-400">
                          ({firstNote})
                        </span>
                      )}
                    </>
                  ),
                  ariaLabel: first && readable ? `${readable} (${firstNote})` : readable,
                  disabled: first,
                };
              })}
            />
          )}
        </div>
        <Textarea
          ref={justificationFieldRef}
          label={
            question.requiresJustification
              ? t("practice.mc.justificationRequired")
              : t("practice.mc.justification")
          }
          required={question.requiresJustification === true}
          value={justification}
          onChange={(event) => onJustificationChange(event.target.value)}
          error={justificationError}
          rows={3}
          maxLength={JUSTIFICATION_MAX}
          onSubmitShortcut={onSubmit}
        />
        {/* On the retry the panel owns the buttons, and no new rating is asked for. */}
        {!retry && rating(onSubmit)}
        {!retry && (
          <Button onClick={onSubmit} loading={submitting} disabled={mcSelected.length === 0}>
            {t("practice.submit")}
          </Button>
        )}
      </div>
    );
  }

  if (question.type === "numeric") {
    const showAllSwitch = showAll && question.parts.length > 1 && !retry && (
      <Checkbox
        label={t("practice.part.showAll")}
        hint={t("practice.part.showAllHint")}
        checked={showAll.checked}
        disabled={showAll.disabled}
        onChange={(event) => showAll.onChange(event.target.checked)}
      />
    );
    if (segmented && !retry) {
      return (
        <div className="mt-4 space-y-3">
          {showAllSwitch}
          <SegmentedNumeric parts={question.parts} figures={question.figures} {...segmented} />
        </div>
      );
    }
    return (
      <div className="mt-4 space-y-3">
        {showAllSwitch}
        {question.parts.map((part) => {
          // On the retry a right part stays locked with what was typed the first time.
          const kept = retry?.keptPartIds.includes(part.id) ?? false;
          const label =
            question.parts.length > 1 ? part.id.toUpperCase() : t("practice.numeric.answer");
          return (
            <div key={part.id}>
              {part.prompt && (
                <div className="mb-1 text-sm text-ink-600 dark:text-ink-400">
                  <Localized value={part.prompt} variant="compact" />
                </div>
              )}
              {question.figures
                .filter((figure) => figure.partId === part.id)
                .map((figure, i) => (
                  <Figure key={i} figure={figure} />
                ))}
              <NumericInput
                value={kept ? (retry?.keptValues[part.id] ?? "") : (numericValues[part.id] ?? "")}
                onChange={(value) => onNumericChange(part.id, value)}
                unit={part.unit}
                label={kept ? `${label} (${t("practice.retry.partKept")})` : label}
                disabled={kept}
                onSubmit={onSubmit}
                describedBy={retry ? undefined : keysHintId}
              />
            </div>
          );
        })}
        {!retry && rating(onSubmit)}
        {!retry && (
          <Button onClick={onSubmit} loading={submitting}>
            {t("practice.submit")}
          </Button>
        )}
      </div>
    );
  }

  // open
  if (result.openRevealPending && result.revealed?.type === "open") {
    const revealed = result.revealed;
    // Sessions saved before `preRevealText` existed fall back to the in-memory draft.
    const locked = result.preRevealText ?? openText;
    // The page's error can outlive the refused save (the student may then tick the rest
    // instead of typing), so the missed field only shows it while it is still required.
    const belowFull = openBelowFullMarks(revealed.rubric, openTicked, question.points);
    // A quick answer satisfies the requirement; the own-words field is then optional.
    const missedRequired = belowFull && !openMissedChip;
    return (
      <div className="mt-4 space-y-4">
        <section
          aria-labelledby={preRevealHeadingId}
          className="rounded-lg border border-ink-200 bg-ink-50 p-4 dark:border-ink-800 dark:bg-ink-900/60"
        >
          <h3
            id={preRevealHeadingId}
            className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200"
          >
            {t("practice.open.preRevealLabel")}
          </h3>
          {locked.trim() ? (
            <p className="whitespace-pre-wrap break-words text-sm text-ink-800 dark:text-ink-100">
              {locked}
            </p>
          ) : (
            <p className="text-sm text-ink-600 dark:text-ink-400">
              {t("practice.open.preRevealEmpty")}
            </p>
          )}
        </section>
        <div className="rounded-lg border border-ink-200 p-4 dark:border-ink-800">
          <h3 className="mb-2 text-sm font-medium text-ink-700 dark:text-ink-200">
            {t("practice.feedback.solution")}
          </h3>
          <Localized value={revealed.solution} />
          {revealed.solutionFigure && <Figure figure={revealed.solutionFigure} />}
          <div className="mt-4 space-y-3">
            <Textarea
              label={t("practice.open.correction")}
              value={openCorrection}
              onChange={(event) => onOpenCorrectionChange(event.target.value)}
              rows={4}
              maxLength={OPEN_TEXT_MAX}
            />
            <RadioGroup<MissedChip>
              name={`missed-chip-${question.id}`}
              legend={t("practice.open.missedChips")}
              value={openMissedChip ?? null}
              onChange={onOpenMissedChipChange}
              className="!flex-row flex-wrap gap-x-5 gap-y-2"
              options={MISSED_CHIPS.map((chip) => ({
                value: chip,
                label: t(`practice.open.missedChip.${chip}`),
              }))}
            />
            <Input
              ref={openMissedFieldRef}
              label={missedRequired ? t("practice.open.missed") : t("practice.open.missedOptional")}
              required={missedRequired}
              value={openMissed}
              onChange={(event) => onOpenMissedChange(event.target.value)}
              error={belowFull && !openMissedChip ? openMissedError : undefined}
              maxLength={OPEN_TEXT_MAX}
            />
          </div>
          <h3 className="mb-1 mt-4 text-sm font-medium text-ink-700 dark:text-ink-200">
            {t("practice.open.rubric")}
          </h3>
          <p className="mb-2 text-sm text-ink-600 dark:text-ink-400">
            {t("practice.open.rubricInstruction")}
          </p>
          <div className="space-y-2">
            {revealed.rubric.map((item) => (
              <Checkbox
                key={item.id}
                label={<Localized value={item.text} variant="inline" />}
                checked={openTicked.includes(item.id)}
                onChange={(event) => {
                  const checked = event.target.checked;
                  onOpenTickedChange(
                    checked ? [...openTicked, item.id] : openTicked.filter((id) => id !== item.id),
                  );
                }}
              />
            ))}
          </div>
          {revealed.finalAnswer && (
            <div className="mt-3 font-medium text-ink-800 dark:text-ink-100">
              <Localized value={revealed.finalAnswer} variant="inline" />
            </div>
          )}
        </div>
        <Button onClick={onOpenSave} loading={submitting}>
          {t("practice.open.save")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      <Textarea
        label={t("practice.open.yourWorking")}
        hint={t("practice.open.yourWorkingHint")}
        value={openText}
        onChange={(event) => onOpenTextChange(event.target.value)}
        rows={6}
        maxLength={OPEN_TEXT_MAX}
        onSubmitShortcut={onOpenKlar}
      />
      {rating(onOpenKlar)}
      <div className="flex flex-wrap gap-2">
        <Button onClick={onOpenKlar} loading={revealing}>
          {t("practice.open.klar")}
        </Button>
        <Button variant="secondary" onClick={onOpenGiveUp} loading={revealing}>
          {t("practice.open.giveUp")}
        </Button>
      </div>
    </div>
  );
}
